/**
 * Exports a logger that dispatches to the browser console and/or Datadog.
 *
 * Console output is enabled in every environment except production (`env-type: production`),
 * covering local development, gamma, staging, PR previews, and demo environments. The level
 * can be configured via `NEXT_PUBLIC_LOG_LEVEL` (`info`, `warn`, `error`, or `off`; default `info`).
 *
 * Datadog logging is enabled in all deployed environments (`NODE_ENV=production`), including
 * gamma, staging, PR previews, and demo environments. Only warnings and errors are forwarded.
 *
 * Usages are safe to commit.
 */
import { datadogLogs } from '@datadog/browser-logs'
import { compact } from 'lodash'
import { useMemo } from 'react'

import { getDatadogLogsConfig } from '~/configs/datadog'
import envConfig from '~/configs/env'

type LogFn = (...args: unknown[]) => void
type LogLevel = 'info' | 'warn' | 'error' | 'off'

// Console: off for production (customer-facing), configurable for all other environments
const consoleLogLevel: LogLevel = (() => {
  if (envConfig.environmentType === 'production') return 'off'

  const envVar = process.env.NEXT_PUBLIC_LOG_LEVEL || 'info'
  if (envVar === 'off' || envVar === 'error' || envVar === 'warn' || envVar === 'info') return envVar

  // Received some unrecognized value, so default to 'off'
  console.warn(`Unrecognized log level: "${envVar}", defaulting to "off"`)
  return 'off'
})()

// Datadog: enabled for all deployed environments (NODE_ENV=production), warn/error only
const datadogLogLevel: LogLevel = process.env.NODE_ENV === 'production' ? 'warn' : 'off'

if (datadogLogLevel !== 'off') {
  datadogLogs.init(getDatadogLogsConfig(envConfig.environment, envConfig.appVersion))
}

function shouldLog(threshold: LogLevel, level: Exclude<LogLevel, 'off'>): boolean {
  if (threshold === 'off') return false
  const levels: Exclude<LogLevel, 'off'>[] = ['info', 'warn', 'error']
  return levels.indexOf(level) >= levels.indexOf(threshold)
}

export class Logger {
  private name: string

  constructor(name: string = 'Logger') {
    this.name = name
  }

  private get prefix() {
    return `[${this.name}]`
  }

  private logFnFactory(level: Exclude<LogLevel, 'off'>): LogFn {
    return (...args) => {
      if (shouldLog(consoleLogLevel, level)) {
        console[level](this.prefix, ...args)
      }
      if (shouldLog(datadogLogLevel, level)) {
        const message = args
          .map((a) => {
            if (typeof a === 'string') return a
            if (a instanceof Error) {
              return compact([`${a.name}: ${a.message}`, a.stack]).join('\n')
            }
            return JSON.stringify(a)
          })
          .join(' ')
        datadogLogs.logger[level](`${this.prefix} ${message}`)
      }
    }
  }

  public readonly info = this.logFnFactory('info')
  public readonly warn = this.logFnFactory('warn')
  public readonly error = this.logFnFactory('error')

  /**
   * Prints tabular data to the browser console via `console.table`. Unlike
   * `info`/`warn`/`error`, this is NOT gated by `consoleLogLevel` and does
   * NOT route to Datadog — `console.table` produces a structured render that
   * wouldn't survive serialization to a log message. Intended for opt-in
   * debug surfaces (e.g. the bootProfiler) where the caller controls when
   * to invoke it.
   */
  public table(rows: unknown, header?: string): void {
    if (header) {
      console.log(`${this.prefix} ${header}`)
    }
    console.table(rows)
  }
}

// Non-hook export for use outside of hooks/components
export const logger = new Logger()

export default function useLogger(name?: string): Logger {
  return useMemo(() => new Logger(name), [name])
}
